import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CloudUpload, Delete, GppGood, Verified } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  createPublicOccurrence,
  CreateOccurrencePayload,
  getPublicCompany,
  OccurrenceTypeOption,
  PublicCompanyView,
  SEVERITY_LABELS,
  uploadPublicEvidences,
} from 'src/apis/eezyguard';

const MAX_FILES = 10;
const MAX_FILE_MB = 25;
const DESCRIPTION_MAX = 5000;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type FormState = {
  is_anonymous: boolean;
  occurrence_type: string;
  occurrence_type_other: string;
  severity: string;
  sector: string;
  location: string;
  location_details: string;
  approximate_date: string;
  description: string;
  people_involved: string;
  witnesses: string;
  additional_information: string;
  reporter_name: string;
  reporter_email: string;
  reporter_phone: string;
  reporter_role: string;
  reporter_relationship: string;
  identity_share_authorized: boolean;
  privacy_accepted: boolean;
  truthfulness_confirmed: boolean;
};

const INITIAL: FormState = {
  is_anonymous: true,
  occurrence_type: '',
  occurrence_type_other: '',
  severity: '',
  sector: '',
  location: '',
  location_details: '',
  approximate_date: '',
  description: '',
  people_involved: '',
  witnesses: '',
  additional_information: '',
  reporter_name: '',
  reporter_email: '',
  reporter_phone: '',
  reporter_role: '',
  reporter_relationship: '',
  identity_share_authorized: false,
  privacy_accepted: false,
  truthfulness_confirmed: false,
};

export default function EezyGuardForm(): JSX.Element {
  const { slug = '' } = useParams();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<PublicCompanyView | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [protocol, setProtocol] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getPublicCompany(slug)
      .then((data) => active && setCompany(data))
      .catch(() => active && setCompany({ available: false }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [slug]);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onToggleAnonymous = useCallback((anonymous: boolean) => {
    setForm((prev) => ({
      ...prev,
      is_anonymous: anonymous,
      // limpa identificação ao voltar para anônimo
      ...(anonymous
        ? {
            reporter_name: '',
            reporter_email: '',
            reporter_phone: '',
            reporter_role: '',
            reporter_relationship: '',
            identity_share_authorized: false,
          }
        : {}),
    }));
  }, []);

  const onSelectFiles = useCallback(
    (selected: FileList | null) => {
      if (!selected) return;
      const incoming = Array.from(selected);
      setError('');
      const tooBig = incoming.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
      if (tooBig) {
        setError(`Cada arquivo deve ter no máximo ${MAX_FILE_MB} MB.`);
        return;
      }
      setFiles((prev) => {
        const merged = [...prev, ...incoming].slice(0, MAX_FILES);
        return merged;
      });
    },
    []
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validate = (): string | null => {
    if (!form.occurrence_type) return 'Selecione o tipo de ocorrência.';
    if (!form.severity) return 'Selecione a gravidade percebida.';
    if (!form.sector.trim()) return 'Informe o setor.';
    if (!form.location.trim()) return 'Informe o local da ocorrência.';
    if (!form.approximate_date) return 'Informe a data aproximada.';
    if (form.description.trim().length < 10)
      return 'A descrição detalhada é obrigatória (mínimo 10 caracteres).';
    if (!form.privacy_accepted)
      return 'É necessário aceitar a política de privacidade e LGPD.';
    if (!form.truthfulness_confirmed)
      return 'É necessário confirmar a veracidade das informações.';
    if (!form.is_anonymous) {
      if (!form.reporter_name.trim())
        return 'Nome é obrigatório em denúncia identificada.';
      if (!form.reporter_email.trim() && !form.reporter_phone.trim())
        return 'Informe ao menos um meio de contato (e-mail ou telefone).';
    }
    return null;
  };

  const buildPayload = (): CreateOccurrencePayload => {
    const base: CreateOccurrencePayload = {
      is_anonymous: form.is_anonymous,
      occurrence_type: form.occurrence_type,
      occurrence_type_other: form.occurrence_type_other || undefined,
      severity: form.severity as CreateOccurrencePayload['severity'],
      sector: form.sector,
      location: form.location,
      location_details: form.location_details || undefined,
      approximate_date: form.approximate_date,
      description: form.description,
      people_involved: form.people_involved || undefined,
      witnesses: form.witnesses || undefined,
      additional_information: form.additional_information || undefined,
      privacy_accepted: form.privacy_accepted,
      truthfulness_confirmed: form.truthfulness_confirmed,
    };
    if (!form.is_anonymous) {
      // envia identificação somente quando não anônimo
      base.reporter_name = form.reporter_name;
      base.reporter_email = form.reporter_email || undefined;
      base.reporter_phone = form.reporter_phone || undefined;
      base.reporter_role = form.reporter_role || undefined;
      base.reporter_relationship = form.reporter_relationship || undefined;
      base.identity_share_authorized = form.identity_share_authorized;
    }
    return base;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { protocol: newProtocol, occurrence_id } = await createPublicOccurrence(
        slug,
        buildPayload()
      );
      if (files.length > 0) {
        setUploadProgress(0);
        await uploadPublicEvidences(slug, occurrence_id, files, setUploadProgress);
      }
      setProtocol(newProtocol);
    } catch (err) {
      setError(
        'Não foi possível registrar sua ocorrência. Verifique os dados e tente novamente.'
      );
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  };

  const descriptionCount = useMemo(
    () => `${form.description.length}/${DESCRIPTION_MAX}`,
    [form.description]
  );

  if (loading) {
    return (
      <CenteredShell>
        <CircularProgress />
      </CenteredShell>
    );
  }

  if (!company?.available) {
    return (
      <CenteredShell>
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <GppGood color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Canal indisponível
          </Typography>
          <Typography color="text.secondary">
            Este canal de denúncias não está disponível no momento. Se você recebeu
            este link de sua empresa, entre em contato com o RH.
          </Typography>
        </Paper>
      </CenteredShell>
    );
  }

  if (protocol) {
    return (
      <CenteredShell>
        <Paper sx={{ p: 4, maxWidth: 520, textAlign: 'center' }}>
          <Verified color="success" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Sua ocorrência foi registrada com segurança.
          </Typography>
          <Chip
            label={`Protocolo: ${protocol}`}
            color="primary"
            sx={{ my: 2, fontWeight: 700, fontSize: '1rem', py: 2.5 }}
          />
          <Typography color="text.secondary" variant="body2">
            Este protocolo serve apenas como referência e comprovante opcional. Não
            existe uma área pública de acompanhamento — guarde-o se desejar.
          </Typography>
        </Paper>
      </CenteredShell>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f4f6f8',
        py: { xs: 2, md: 5 },
      }}
    >
      <Container maxWidth="sm">
        <Stack alignItems="center" spacing={1} mb={3}>
          <GppGood color="primary" sx={{ fontSize: 44 }} />
          <Typography variant="h5" fontWeight={700}>
            EezyLine
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            Canal de denúncias — {company.company_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Este é um canal seguro e confidencial. Você pode denunciar de forma
            totalmente anônima.
          </Typography>
        </Stack>

        <Paper component="form" onSubmit={onSubmit} sx={{ p: { xs: 2, md: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={!form.is_anonymous}
                  onChange={(e) => onToggleAnonymous(!e.target.checked)}
                />
              }
              label={
                form.is_anonymous
                  ? 'Denúncia anônima (recomendado)'
                  : 'Denúncia identificada'
              }
            />

            <TextField
              select
              required
              label="Tipo de ocorrência"
              value={form.occurrence_type}
              onChange={(e) => set('occurrence_type', e.target.value)}
              fullWidth
            >
              {(company.occurrence_types ?? []).map((opt: OccurrenceTypeOption) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {form.occurrence_type === 'outras_ocorrencias' && (
              <TextField
                label="Descreva o tipo (opcional)"
                value={form.occurrence_type_other}
                onChange={(e) => set('occurrence_type_other', e.target.value)}
                fullWidth
              />
            )}

            <TextField
              select
              required
              label="Gravidade percebida"
              value={form.severity}
              onChange={(e) => set('severity', e.target.value)}
              fullWidth
            >
              {(company.severities ?? []).map((sev) => (
                <MenuItem key={sev} value={sev}>
                  {SEVERITY_LABELS[sev]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              required
              label="Setor"
              value={form.sector}
              onChange={(e) => set('sector', e.target.value)}
              fullWidth
            />
            <TextField
              required
              label="Local da ocorrência"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              fullWidth
            />
            <TextField
              label="Complemento do local (opcional)"
              value={form.location_details}
              onChange={(e) => set('location_details', e.target.value)}
              fullWidth
            />
            <TextField
              required
              type="date"
              label="Data aproximada"
              value={form.approximate_date}
              onChange={(e) => set('approximate_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              required
              label="Descrição detalhada"
              value={form.description}
              onChange={(e) =>
                set('description', e.target.value.slice(0, DESCRIPTION_MAX))
              }
              helperText={descriptionCount}
              FormHelperTextProps={{ sx: { textAlign: 'right' } }}
              multiline
              minRows={4}
              fullWidth
            />
            <TextField
              label="Pessoas envolvidas (opcional)"
              value={form.people_involved}
              onChange={(e) => set('people_involved', e.target.value)}
              multiline
              fullWidth
            />
            <TextField
              label="Testemunhas (opcional)"
              value={form.witnesses}
              onChange={(e) => set('witnesses', e.target.value)}
              multiline
              fullWidth
            />
            <TextField
              label="Informações adicionais (opcional)"
              value={form.additional_information}
              onChange={(e) => set('additional_information', e.target.value)}
              multiline
              fullWidth
            />

            {!form.is_anonymous && (
              <>
                <Divider>Identificação</Divider>
                <Alert severity="info">
                  Seus dados serão tratados de forma confidencial e ficam restritos à
                  Eezycare, salvo autorização explícita de compartilhamento.
                </Alert>
                <TextField
                  required
                  label="Nome"
                  value={form.reporter_name}
                  onChange={(e) => set('reporter_name', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="E-mail"
                  type="email"
                  value={form.reporter_email}
                  onChange={(e) => set('reporter_email', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Telefone"
                  value={form.reporter_phone}
                  onChange={(e) => set('reporter_phone', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Cargo (opcional)"
                  value={form.reporter_role}
                  onChange={(e) => set('reporter_role', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Relação com a empresa (opcional)"
                  value={form.reporter_relationship}
                  onChange={(e) => set('reporter_relationship', e.target.value)}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.identity_share_authorized}
                      onChange={(e) =>
                        set('identity_share_authorized', e.target.checked)
                      }
                    />
                  }
                  label="Autorizo que minha identidade seja compartilhada com a empresa durante a tratativa."
                />
              </>
            )}

            <Divider>Evidências (opcional)</Divider>
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                disabled={files.length >= MAX_FILES}
              >
                Anexar arquivos
                <VisuallyHiddenInput
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*,application/pdf"
                  onChange={(e) => onSelectFiles(e.target.files)}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Até {MAX_FILES} arquivos, {MAX_FILE_MB} MB cada. Imagens, vídeos, áudios
                e PDF.
              </Typography>
              <Stack spacing={1} mt={1}>
                {files.map((file, index) => (
                  <Box
                    key={`${file.name}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      {file.type.startsWith('image/') && (
                        <Box
                          component="img"
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                        />
                      )}
                      <Typography variant="body2" noWrap>
                        {file.name}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeFile(index)} aria-label="remover anexo">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.privacy_accepted}
                    onChange={(e) => set('privacy_accepted', e.target.checked)}
                  />
                }
                label="Li e aceito a política de privacidade e o tratamento de dados conforme a LGPD."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.truthfulness_confirmed}
                    onChange={(e) => set('truthfulness_confirmed', e.target.checked)}
                  />
                }
                label="Confirmo que as informações prestadas são verdadeiras."
              />
            </FormGroup>

            {uploadProgress !== null && (
              <Box>
                <FormLabel>Enviando anexos… {uploadProgress}%</FormLabel>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : undefined}
            >
              {submitting ? 'Enviando…' : 'Enviar denúncia'}
            </Button>
          </Stack>
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          mt={2}
        >
          O EezyLine auxilia na organização e rastreabilidade das ocorrências. Não
          substitui assessoria jurídica.
        </Typography>
      </Container>
    </Box>
  );
}

function CenteredShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}
